// The collect looks of the World fields `seaMineCollect` and
// `deployableCollect` (v2-the-deep.md §11.8, mockup rounds 073 and 074,
// R2538, R2652).
//
// A collect look is what a Sapper at rest draws while it takes sea mines or
// its own deployables aboard (§11.1 COLLECT): which items come aboard, the
// order, the item with no room, the hostile item it may not take, and the
// stow as it fills. Each look ports its card of
// `archive/mockups/073-sapper-collect-mines.html` or
// `074-sapper-collect-deployables.html` point for point.
//
// These are the marks alone. The rule of the take is the server's: the
// Sapper takes the nearest item first while its stow has room (R2635,
// `sea_objects.rules.collect_order`). `planSeaMineTake` and
// `planDeployableTake` state that rule for the picture, so the look shows
// the take the server makes; the layer that draws sea objects on the map
// fills them from the State frame. Each look keeps its own timeline in
// seconds of the look: the collect of round 073 plays in `COLLECT_SPAN`
// seconds, and the layer maps the 20 map seconds of an item to it. The
// round pages drew the sea mine and the deployable with a placeholder mark;
// this module keeps that placeholder, and R2652 says how the client lane
// puts the marks of the `seaMine` and the `deployable` field in its place.
//
// This is a leaf (`leaves.test.ts`): it paints into a context it is handed.

import { DEPLOYABLE_COLLECT_STYLES, SEA_MINE_COLLECT_STYLES } from '../gen/balance';
import { seamarks as WORDS } from '../gen/strings';
import {
  Fl, S, SEA_INK, arc, bandPx, circle, disc, fade, hexPath, inCubic, inOut, lerp, line, lw, outCubic, px, smooth,
  text, wordFont, type ChartStep, type Ctx, type Pt,
} from './seaobject-kit';
import { TAU, clamp01 } from './math';
import { rgba } from './color';
import { hmmss } from '../locale/clock';
import type { DeployableKind } from './deployable-marks';
import type { SeaMineKind } from './seamine-marks';

/** One look of the `seaMineCollect` field. */
export type SeaMineCollectStyle = (typeof SEA_MINE_COLLECT_STYLES)[number];
/** One look of the `deployableCollect` field. */
export type DeployableCollectStyle = (typeof DEPLOYABLE_COLLECT_STYLES)[number];

const WHITE = SEA_INK.white, PALE = SEA_INK.pale, DEEP = SEA_INK.deep, DIM = SEA_INK.dim, FAINT = SEA_INK.faint;
const PI = Math.PI;

/**
 * The one point a `pos` hook answers, kept from frame to frame: the caller
 * reads it before it asks for the next one. `at`, `polarAt` and
 * `toHullAt` write it.
 */
const PT: [number, number] = [0, 0];
const at = (x: number, y: number): Pt => { PT[0] = x; PT[1] = y; return PT; };
const polarAt = (a: number, r: number): Pt => at(Math.cos(a) * r, Math.sin(a) * r);

/** The four corners of a box, clockwise from the top left: the signs of x
 *  and of y. */
const CORNER_X = [-1, 1, 1, -1] as const, CORNER_Y = [-1, -1, 1, 1] as const;

/** The path of `P` through three points, with no array of points. */
function path3(c: Ctx, x0: number, y0: number, x1: number, y1: number, x2: number, y2: number, close = true): void {
  c.beginPath(); c.moveTo(x0, y0); c.lineTo(x1, y1); c.lineTo(x2, y2);
  if (close) c.closePath();
}

/** The closed path of `P` through four points, with no array of points. */
function path4(c: Ctx, x0: number, y0: number, x1: number, y1: number, x2: number, y2: number, x3: number, y3: number): void {
  c.beginPath(); c.moveTo(x0, y0); c.lineTo(x1, y1); c.lineTo(x2, y2); c.lineTo(x3, y3); c.closePath();
}

// =================================================================================
// Round 073: a Sapper collects sea mines
// =================================================================================

/** The seconds the collect of round 073 plays in. */
export const COLLECT_SPAN = 3.0;

/** One sea mine of the field, in the frame of the Sapper at rest. */
export interface FieldMine {
  id: number;
  kind: SeaMineKind;
  side: 'own' | 'foe';
  /** The distance and the bearing from the Sapper, and the point. */
  d: number; a: number; x: number; y: number;
  /** The cargo of the kind (seaobjects.toml). */
  cargo: number;
}

/** One sea mine inside the collect radius: its place in the order, and
 *  whether it comes aboard. */
export interface TakeEntry {
  m: FieldMine;
  /** The place in the order of the take, nearest first. */
  i: number;
  take: boolean;
  /** The stow before it, and after it when it comes aboard. */
  load0: number; load1: number;
  /** The place among the sea mines that come aboard. */
  j: number;
}

/** The take of one collect. */
export interface TakePlan {
  R: number;
  ins: TakeEntry[];
  takes: TakeEntry[];
  lefts: TakeEntry[];
  /** The stow after the collect. */
  final: number;
  byId: ReadonlyMap<number, TakeEntry>;
}

const byDistance = (a: TakeEntry, b: TakeEntry): number => a.m.d - b.m.d;

/**
 * The take of the rule (R2635): every sea mine inside the radius, nearest
 * first; it comes aboard while the stow has room for its cargo, and a sea
 * mine with no room stays where it lies.
 */
export function planSeaMineTake(field: readonly FieldMine[], R: number, stow0: number, cap: number): TakePlan {
  // One array: the entries of the sea mines inside the radius, sorted in
  // place. The sort is stable, so a tie keeps the order of the field.
  const ins: TakeEntry[] = [];
  for (const m of field) if (m.d <= R) ins.push({ m, i: 0, take: false, load0: 0, load1: 0, j: -1 });
  ins.sort(byDistance);
  for (let i = 0; i < ins.length; i++) ins[i].i = i;
  let load = stow0;
  const takes: TakeEntry[] = [], lefts: TakeEntry[] = [], byId = new Map<number, TakeEntry>();
  for (const e of ins) {
    e.load0 = load; byId.set(e.m.id, e);
    if (load + e.m.cargo <= cap) { e.take = true; load += e.m.cargo; e.load1 = load; e.j = takes.length; takes.push(e); }
    else lefts.push(e);
  }
  return { R, ins, takes, lefts, final: load, byId };
}

/** One look's schedule: when the effect reaches each sea mine (`hit`), when
 *  it is aboard (`arr`), and a disarm time of its own (`dis`). A look adds
 *  the figures its own drawing reads. */
export interface Schedule {
  hit: Map<number, number>;
  arr: Map<number, number>;
  dis: Map<number, number>;
  /** Card 01: the radius of the closing ring at `k`. */
  r?: (k: number) => number;
  /** Card 04: the half gap of the two fronts at `k`. */
  yf?: (k: number) => number;
  /** Card 07: the radius of the ping at `k`. */
  ping?: (k: number) => number;
  /** Card 16: the cells of the hex grid, the front, the nearest cell. */
  cells?: { x: number; y: number; rho: number }[];
  rf?: (k: number) => number;
  near?: (x: number, y: number) => { x: number; y: number; rho: number };
  home?: Map<number, { x: number; y: number; rho: number }>;
  /** Card 19: the moment each figure starts. */
  tj?: Map<number, number>;
}

/** One item of the stow: the sea mines aboard before the collect, then
 *  each one that arrives. */
export interface StowItem { kind: SeaMineKind; cargo: number }

/** Everything a sea mine collect look reads. The Sapper is at rest at the
 *  origin, bow to +x. */
export interface CollectScene {
  /** The seconds since the collect started; negative before. */
  k: number;
  R: number;
  P: TakePlan;
  Q: Schedule;
  /** The stow before the collect, and the cargo of the Sapper. */
  stow0: readonly StowItem[];
  cap: number;
  /** The own ink and the ink of the side of a hostile sea mine. */
  ownCol: number;
  foeCol: number;
  z: number;
  face: string;
  /** The turn of the frame of the look on the map, in radians: the
   *  heading of the Sapper at rest. A figure turns back by it, so it reads
   *  upright and level at every heading (R2711). None is a frame that is
   *  not turned, as on the round pages. */
  head?: number;
  /** The mark of the `seaMine` field, which stands in place of the
   *  placeholder at `x, y` (R2657): `q` runs from 0 (armed, hostile) to 1
   *  (disarmed, own), at alpha `a` and scale `s`. */
  mark?: (c: Ctx, m: FieldMine, x: number, y: number, q: number, a: number, s: number) => void;
  /** The wall seconds the work still runs (a SEARCH, R2833). The look then
   *  draws this timer below the hull on the map, and the `num` display
   *  draws no stow figure. None is a look with no timer. */
  timer?: number;
}

/** The stow at the collect time: the items aboard, each with its fill. */
interface StowState {
  items: { kind: SeaMineKind; cargo: number; f: number; fresh: boolean }[];
  load: number;
  freshFrom: number;
  full: boolean;
}

type StowItemState = StowState['items'][number];

/** The stow of the frame: one state, its items kept from frame to frame.
 *  A stow display reads it before the next frame writes it again. */
const STOW: StowState = { items: [], load: 0, freshFrom: 0, full: false };
const STOW_POOL: StowItemState[] = [];
/** The takes in the order they come aboard, kept from frame to frame. */
const BY_ARRIVAL: TakeEntry[] = [];
let arrivalQ: Schedule | null = null;
const arrivalOf = (e: TakeEntry): number => arrivalQ!.arr.get(e.m.id) ?? Infinity;
const byArrival = (a: TakeEntry, b: TakeEntry): number => arrivalOf(a) - arrivalOf(b);

function stowItem(i: number, kind: SeaMineKind, cargo: number, f: number, fresh: boolean): StowItemState {
  let it = STOW_POOL[i];
  if (!it) { it = { kind, cargo, f, fresh }; STOW_POOL[i] = it; return it; }
  it.kind = kind; it.cargo = cargo; it.f = f; it.fresh = fresh;
  return it;
}

function stowState(X: CollectScene): StowState {
  const items = STOW.items;
  items.length = 0;
  for (const s of X.stow0) items.push(stowItem(items.length, s.kind, s.cargo, 1, false));
  BY_ARRIVAL.length = 0;
  for (const e of X.P.takes) BY_ARRIVAL.push(e);
  arrivalQ = X.Q;
  BY_ARRIVAL.sort(byArrival);
  arrivalQ = null;
  for (const e of BY_ARRIVAL) {
    const a = X.Q.arr.get(e.m.id) ?? Infinity;
    items.push(stowItem(items.length, e.m.kind, e.m.cargo, X.k < 0 ? 0 : smooth(a, a + 0.22, X.k), X.k >= a && X.k < a + 0.6));
  }
  BY_ARRIVAL.length = 0;
  let load = 0, fresh = 0;
  for (const it of items) { load += it.cargo * it.f; if (it.fresh) fresh += it.cargo * it.f; }
  STOW.load = load; STOW.freshFrom = load - fresh; STOW.full = load >= X.cap - 0.01;
  return STOW;
}

/** The first k in [k0, k1] for which `test(k)` holds. */
function firstK(test: (k: number) => boolean, k0 = 0, k1 = COLLECT_SPAN + 0.6): number {
  for (let k = k0; k <= k1; k += 0.004) if (test(k)) return k;
  return Infinity;
}

/** The placeholder sea mine of the round: a disc and a ring. `q` runs from
 *  0 (armed, hostile) to 1 (disarmed, own): the solid ring breaks into a
 *  dashed one in the own ink. */
export function collectMine(c: Ctx, X: CollectScene, m: FieldMine, x: number, y: number, q: number, a = 1, s = 1): void {
  if (!(a > 0)) return;
  if (X.mark) { X.mark(c, m, x, y, clamp01(q), a, s); return; }
  const r = (m.kind === 'petard' ? 4 : 3) * s, OWN = X.ownCol, F = X.foeCol;
  if (m.side === 'own') { disc(c, x, y, r, OWN, a); circle(c, x, y, 5 * s, OWN, 0.9 * a, lw(1, X.z)); return; }
  const v = clamp01(q);
  disc(c, x, y, r, F, a * (1 - v)); disc(c, x, y, r, OWN, a * v);
  circle(c, x, y, 5 * s, F, 0.9 * a * (1 - v), lw(1, X.z));
  circle(c, x, y, 5 * s, OWN, a * v, lw(1.1, X.z), [px(2.2, X.z), px(1.8, X.z)]);
}

function flashRing(c: Ctx, x: number, y: number, u: number, z: number, r0 = 5, r1 = 9.5): void {
  if (!(u >= 0 && u < 1)) return;
  circle(c, x, y, lerp(r0, r1, u), WHITE, 1 - u, lw(1.1, z));
}

/** A tell keeps its screen size below 1x, so it still reads at 0.5x. */
const ts = (z: number): number => 1 / Math.min(1, z);

/** The no-room tell: a struck stow box beside the sea mine. */
function struckBox(c: Ctx, x: number, y: number, a: number, z: number): void {
  if (!(a > 0)) return;
  const s = 2.3 * ts(z), ox = x + 7.5 * ts(z), oy = y - 7.5 * ts(z);
  c.beginPath(); c.rect(ox - s, oy - s, 2 * s, 2 * s); Fl(c, DEEP, 0.6 * a); S(c, WHITE, 0.95 * a, lw(0.9, z));
  line(c, ox - s - 1.2 * ts(z), oy + s + 1.2 * ts(z), ox + s + 1.2 * ts(z), oy - s - 1.2 * ts(z), WHITE, 0.95 * a, lw(1.1, z));
}

function jitter(k: number, h: number): number {
  const u = k - h;
  return u > 0 && u < 0.35 ? Math.sin(u * 60) * 0.9 * (1 - u / 0.35) : 0;
}

function hitOf(X: CollectScene, e: TakeEntry): number { return X.Q.hit.get(e.m.id) ?? Infinity; }
function arrOf(X: CollectScene, e: TakeEntry): number { return X.Q.arr.get(e.m.id) ?? Infinity; }

/** A sea mine with no room: it shakes when the effect reaches it, and the
 *  struck stow box stands beside it. */
function defaultLeft(c: Ctx, e: TakeEntry, X: CollectScene): void {
  const h = hitOf(X, e), k = X.k;
  collectMine(c, X, e.m, e.m.x + jitter(k, h), e.m.y, 0, 1, 1);
  if (k >= h) struckBox(c, e.m.x, e.m.y, smooth(h, h + 0.2, k), X.z);
}

function bracketsAt(c: Ctx, x: number, y: number, hs: number, ink: number, a: number, z: number, dash: readonly number[] = []): void {
  if (!(a > 0)) return;
  const l = hs * 0.45;
  c.beginPath();
  for (let i = 0; i < 4; i++) {
    const qx = CORNER_X[i], qy = CORNER_Y[i];
    c.moveTo(x + qx * hs, y + qy * (hs - l)); c.lineTo(x + qx * hs, y + qy * hs); c.lineTo(x + qx * (hs - l), y + qy * hs);
  }
  S(c, ink, a, lw(1, z), dash);
}

/**
 * A world-placed word of a look in its chart text step, in the mono face
 * (R3249, ui-map-interface.md §17.4): its screen size stays in the band of
 * the step, and under the zoom gate of the step it draws nothing. A cargo
 * figure and a multiplier are notes; a figure the stow display or the
 * timer reads, and a warning, are labels.
 */
function word(
  c: Ctx, s: string, x: number, y: number, step: ChartStep, X: { z: number; face: string }, ink: number, a: number,
  align: CanvasTextAlign = 'center',
): void {
  const wp = bandPx('mono', step, X.z);
  if (wp > 0) text(c, s, x, y, wordFont('mono', wp, X.face), ink, a, align);
}

/**
 * Paints `fn` with the frame turned back by `head` about the point x, y,
 * so a figure the look sets at that point reads upright and level on the
 * map while the frame turns with the Sapper (R2711). The point stays where
 * the look puts it; only the text turns. A frame with no turn paints `fn`
 * as it stands.
 */
function upright(c: Ctx, head: number | undefined, x: number, y: number, fn: () => void): void {
  if (!head) { fn(); return; }
  c.save(); c.translate(x, y); c.rotate(-head); c.translate(-x, -y);
  fn();
  c.restore();
}

function struckFig(c: Ctx, s: string, x: number, y: number, a: number, X: CollectScene): void {
  const wp = bandPx('mono', 'note', X.z);
  if (!(a > 0) || !(wp > 0)) return;
  const f = wordFont('mono', wp, X.face);
  upright(c, X.head, x, y, () => {
    text(c, s, x, y, f, PALE, a, 'left');
    c.font = f;
    const w = c.measureText(s).width;
    line(c, x - px(1, X.z), y, x + w + px(1, X.z), y, WHITE, 0.9 * a, lw(1, X.z));
  });
}

function ringR(c: Ctx, X: CollectScene, a: number, dash?: readonly number[]): void {
  circle(c, 0, 0, X.R, PALE, a, lw(1, X.z), dash ?? [px(3, X.z), px(4, X.z)]);
}
function env(k: number, a: number, b: number, c2: number, d: number): number { return smooth(a, b, k) * (1 - smooth(c2, d, k)); }
const toHullAt = (e: TakeEntry, s: number): Pt => at(e.m.x * (1 - s), e.m.y * (1 - s));

/** The stow figure stands clear of the contact ring at every zoom. */
const numY = (z: number): number => -(21 + px(7, z));

/**
 * The timer of a work: the wall seconds left (M:SS, H:MM:SS from one hour
 * on), below the hull on the map at every heading. It rounds up, so it
 * never reads 0:00 early. Below a display under the hull it stands clear
 * of that display.
 */
function paintTimer(c: Ctx, X: CollectScene, sec: number, under: boolean): void {
  const y = (under ? 38 : 21) + px(7, X.z);
  upright(c, X.head, 0, 0, () => {
    word(c, hmmss(Math.ceil(Math.max(0, sec))), 0, y, 'label', X, X.ownCol, 1);
  });
}

/** The stow displays the cards pick from, in hull space. */
type StowDisplay = (c: Ctx, X: CollectScene, s: StowState) => void;
const STOW_DISPLAYS: Record<'bar' | 'pips' | 'ring' | 'num' | 'hex', StowDisplay> = {
  // A bar of the cargo under the hull, one segment an item, a tick each tenth.
  bar(c, X, s) {
    const W = 40, H = 3.6, x0 = -W / 2, y0 = 25;
    c.beginPath(); c.rect(x0 - 1, y0 - 1, W + 2, H + 2); Fl(c, DEEP, 0.6); S(c, s.full ? WHITE : PALE, s.full ? 0.95 : 0.5, lw(0.8, X.z));
    let cum = 0;
    for (const it of s.items) {
      const w = ((W * it.cargo) / X.cap) * it.f;
      if (w > 0.05) { c.fillStyle = rgba(it.fresh ? WHITE : X.ownCol, 0.92); c.fillRect(x0 + (W * cum) / X.cap + 0.35, y0, Math.max(0.2, w - 0.7), H); }
      cum += it.cargo * it.f;
    }
    for (let i = 1; i < 10; i++) line(c, x0 + (W * i) / 10, y0 + H + 1.4, x0 + (W * i) / 10, y0 + H + 2.6, FAINT, 0.9, lw(0.6, X.z));
  },
  // Ten pips of a tenth under the hull.
  pips(c, X, s) {
    const n = 10, cw = 3.6, g = 1, W = n * cw + (n - 1) * g, x0 = -W / 2, y0 = 25, unit = X.cap / n;
    for (let i = 0; i < n; i++) {
      const x = x0 + i * (cw + g), f = clamp01(s.load / unit - i), fr = i + 1 > s.freshFrom / unit + 0.001;
      c.beginPath(); c.rect(x, y0, cw, cw); Fl(c, DEEP, 0.6); S(c, s.full ? WHITE : PALE, s.full ? 0.9 : 0.45, lw(0.7, X.z));
      if (f > 0) { c.fillStyle = rgba(fr ? WHITE : X.ownCol, 0.92); c.fillRect(x + 0.5, y0 + 0.5, (cw - 1) * f, cw - 1); }
    }
  },
  // Arcs on the contact ring, clockwise from north, one arc an item.
  ring(c, X, s) {
    let a0 = -PI / 2;
    const gap = 0.06;
    for (const it of s.items) {
      const sw = ((TAU * it.cargo) / X.cap) * it.f;
      if (sw > gap) arc(c, 0, 0, 19, a0 + gap / 2, a0 + sw - gap / 2, it.fresh ? WHITE : X.ownCol, 0.95, lw(2.4, X.z));
      a0 += sw;
    }
    if (s.full) circle(c, 0, 0, 22, WHITE, 0.55, lw(0.8, X.z));
  },
  // A figure over the hull: the load and the cargo. The pair turns back
  // about the hull, so it stands over the hull on the map, level, at every
  // heading (R2711).
  num(c, X, s) {
    const v = Math.round(s.load), fresh = s.freshFrom < s.load - 0.5, y = numY(X.z);
    upright(c, X.head, 0, 0, () => {
      word(c, String(v), px(1, X.z), y, 'label', X, fresh ? WHITE : X.ownCol, 1, 'right');
      word(c, WORDS.of_cap({ cap: X.cap }), px(2, X.z), y + px(0.5, X.z), 'note', X, s.full ? WHITE : DIM, 1, 'left');
    });
  },
  // A strip of ten small hexes under the hull.
  hex(c, X, s) {
    const n = 10, r = 2.3, sp = 4.6, x0 = (-sp * (n - 1)) / 2, y0 = 27.5, unit = X.cap / n;
    for (let i = 0; i < n; i++) {
      const x = x0 + i * sp, f = clamp01(s.load / unit - i), fr = i + 1 > s.freshFrom / unit + 0.001;
      hexPath(c, x, y0, r); Fl(c, DEEP, 0.6); S(c, s.full ? WHITE : PALE, s.full ? 0.9 : 0.45, lw(0.7, X.z));
      if (f > 0) {
        c.save(); hexPath(c, x, y0, r - 0.4); c.clip();
        c.fillStyle = rgba(fr ? WHITE : X.ownCol, 0.92); c.fillRect(x - r, y0 - r, 2 * r * f, 2 * r); c.restore();
      }
    }
  },
};

/** One sea mine collect look: its stow display, its schedule, and the hooks
 *  its card fills. */
export interface SeaMineCollectLook {
  stow: keyof typeof STOW_DISPLAYS;
  prep(P: TakePlan, R: number): Schedule;
  /** The point of a sea mine now. It may answer the one kept point of
   *  the module, so the caller reads it before the next ask. */
  pos?(e: TakeEntry, X: CollectScene): Pt;
  mine?(c: Ctx, e: TakeEntry, p: Pt, q: number, X: CollectScene): void;
  left?(c: Ctx, e: TakeEntry, X: CollectScene): void;
  under?(c: Ctx, X: CollectScene): void;
  over?(c: Ctx, X: CollectScene): void;
}

const sched = (): Schedule => ({ hit: new Map(), arr: new Map(), dis: new Map() });

/** Card 01 Closing Ring: a white ring at the collect radius closes on the
 *  hull and carries each sea mine it reaches. */
const closingRing: SeaMineCollectLook = {
  stow: 'ring',
  prep(P, R) {
    const r = (k: number): number => (k < 0.45 ? R : lerp(R, 6, inOut((k - 0.45) / 1.9)));
    const Q = sched(); Q.r = r;
    const ka = firstK((k) => k > 0.45 && r(k) <= 8);
    for (const e of P.ins) {
      Q.hit.set(e.m.id, firstK((k) => k >= 0.45 && r(k) <= e.m.d));
      if (e.take) Q.arr.set(e.m.id, ka);
    }
    return Q;
  },
  pos(e, X) { return polarAt(e.m.a, Math.min(e.m.d, X.Q.r!(X.k))); },
  under(c, X) {
    const k = X.k;
    if (k < 0 || k > 2.8) return;
    const r = X.Q.r!(k), a = env(k, 0, 0.25, 2.3, 2.6);
    ringR(c, X, 0.3 * env(k, 0, 0.25, 2.4, 2.8));
    disc(c, 0, 0, r, X.ownCol, 0.06 * a);
    circle(c, 0, 0, r, WHITE, 0.85 * a, lw(1.4, X.z));
  },
};

/** Card 03 Tractor Beams: a beam runs to each sea mine that comes aboard,
 *  dashed to a hostile one until it is disarmed; then each moves in along
 *  its beam. */
const tractorBeams: SeaMineCollectLook = {
  stow: 'pips',
  prep(P) {
    const Q = sched();
    for (const e of P.ins) {
      Q.hit.set(e.m.id, 0.5);
      if (e.take) { Q.dis.set(e.m.id, 0.55); Q.arr.set(e.m.id, 0.95 + e.m.d / 45); }
    }
    return Q;
  },
  pos(e, X) { return X.k < 0.95 ? at(e.m.x, e.m.y) : polarAt(e.m.a, Math.max(0, e.m.d - 45 * (X.k - 0.95))); },
  under(c, X) {
    const k = X.k;
    if (k < 0) return;
    ringR(c, X, 0.3 * env(k, 0, 0.3, 2.3, 2.7));
    for (const e of X.P.takes) {
      const arr = arrOf(X, e);
      if (k < 0.1 || k >= arr) continue;
      const f = outCubic((k - 0.1) / 0.4), p = k < 0.95 ? at(e.m.x, e.m.y) : polarAt(e.m.a, Math.max(0, e.m.d - 45 * (k - 0.95)));
      const ex = p[0] * f, ey = p[1] * f, armed = e.m.side === 'foe' && k < 0.8;
      line(c, 0, 0, ex, ey, X.ownCol, 0.16, lw(3.2, X.z));
      line(c, 0, 0, ex, ey, armed ? WHITE : PALE, armed ? 0.85 : 0.8, lw(1, X.z), armed ? [px(2.5, X.z), px(2, X.z)] : []);
    }
  },
};

/** Card 04 Keel Squeeze: two fronts close from port and starboard onto the
 *  keel line, and the sea mines slide along the keel into the stow. */
const keelSqueeze: SeaMineCollectLook = {
  stow: 'num',
  prep(P, R) {
    const yf = (k: number): number => R * (1 - smooth(0.3, 1.7, k));
    const Q = sched(); Q.yf = yf;
    for (const e of P.ins) {
      Q.hit.set(e.m.id, firstK((k) => k >= 0.3 && yf(k) <= Math.abs(e.m.y)));
      if (e.take) Q.arr.set(e.m.id, 1.75 + Math.abs(e.m.x + 2.5) / 50 + 0.04);
    }
    return Q;
  },
  pos(e, X) {
    const k = X.k, m = e.m;
    if (k < hitOf(X, e)) return at(m.x, m.y);
    if (k < 1.75) return at(m.x, Math.sign(m.y) * Math.min(Math.abs(m.y), X.Q.yf!(k)));
    const dx = -2.5 - m.x;
    return at(m.x + Math.sign(dx) * Math.min(Math.abs(dx), 50 * (k - 1.75)), 0);
  },
  under(c, X) {
    const k = X.k, R = X.R;
    if (k < 0) return;
    ringR(c, X, 0.3 * env(k, 0, 0.3, 2.4, 2.8));
    if (k > 0.2 && k < 1.95) {
      const a = env(k, 0.2, 0.4, 1.7, 1.95), y = X.Q.yf!(k), hc = Math.sqrt(Math.max(0, R * R - y * y));
      c.save(); c.beginPath(); c.arc(0, 0, R, 0, TAU); c.clip(); c.fillStyle = rgba(X.ownCol, 0.07 * a);
      c.fillRect(-R, -R, 2 * R, R - y); c.fillRect(-R, y, 2 * R, R - y); c.restore();
      line(c, -hc, -y, hc, -y, WHITE, 0.85 * a, lw(1.3, X.z)); line(c, -hc, y, hc, y, WHITE, 0.85 * a, lw(1.3, X.z));
    }
    if (k > 1.6 && k < 2.8) {
      let lo = 0, hi = 0;
      for (const e of X.P.takes) if (k < arrOf(X, e)) { lo = Math.min(lo, e.m.x); hi = Math.max(hi, e.m.x); }
      line(c, lo, 0, hi, 0, PALE, 0.5 * env(k, 1.6, 1.8, 2.5, 2.8), lw(1, X.z));
    }
  },
};

/** Card 07 Ping And Pull: a ping runs out to the collect radius and tags each
 *  sea mine with its cargo figure; then the tagged sea mines move in. */
const pingAndPull: SeaMineCollectLook = {
  stow: 'num',
  prep(P, R) {
    const ping = (k: number): number => R * outCubic(k / 0.7);
    const Q = sched(); Q.ping = ping;
    for (const e of P.ins) {
      Q.hit.set(e.m.id, firstK((k) => ping(k) >= e.m.d));
      if (e.take) Q.arr.set(e.m.id, 2.2);
    }
    return Q;
  },
  pos(e, X) { return X.k < 1.3 ? at(e.m.x, e.m.y) : toHullAt(e, inCubic((X.k - 1.3) / 0.9)); },
  under(c, X) {
    const k = X.k, R = X.R;
    if (k < 0) return;
    if (k < 0.7) circle(c, 0, 0, X.Q.ping!(k), WHITE, 0.9 - (0.4 * k) / 0.7, lw(1.3, X.z));
    else if (k < 2.4) {
      circle(c, 0, 0, R, WHITE, (0.3 + 0.6 * (1 - smooth(0.7, 1.0, k))) * (1 - smooth(2.1, 2.4, k)), lw(lerp(2, 1, smooth(0.7, 1, k)), X.z));
    }
  },
  over(c, X) {
    const k = X.k;
    if (k < 0) return;
    for (const e of X.P.takes) {
      const h = hitOf(X, e), arr = arrOf(X, e);
      if (k < h || k >= arr) continue;
      const p = k < 1.3 ? at(e.m.x, e.m.y) : toHullAt(e, inCubic((k - 1.3) / 0.9));
      const hs = (7 + 4 * (1 - smooth(h, h + 0.2, k))) * (1 - 0.5 * smooth(1.3, 2.2, k));
      bracketsAt(c, p[0], p[1], hs, WHITE, 0.9, X.z);
      // The cargo figure stands level beside its bracket (R2711).
      if (k < 1.3) {
        upright(c, X.head, p[0], p[1], () => word(c, WORDS.plus({ n: e.m.cargo }), p[0] + hs + px(2, X.z), p[1] - hs,
          'note', X, PALE, smooth(h + 0.05, h + 0.2, k), 'left'));
      }
    }
  },
  left(c, e, X) {
    const h = hitOf(X, e), k = X.k;
    collectMine(c, X, e.m, e.m.x + jitter(k, h), e.m.y, 0, 1, 1);
    if (k >= h) {
      const a = smooth(h, h + 0.2, k);
      bracketsAt(c, e.m.x, e.m.y, 7, WHITE, 0.7 * a, X.z, [px(1.5, X.z), px(1.5, X.z)]);
      struckFig(c, String(e.m.cargo), e.m.x + 7 + px(5, X.z), e.m.y - 7 - px(3, X.z), a, X);
    }
  },
};

/** Card 11 Clock Rim: a white arc runs once round the rim and ticks each sea
 *  mine it passes; when the rim closes, every ticked sea mine comes aboard
 *  at once. */
const clockRim: SeaMineCollectLook = {
  stow: 'bar',
  prep(P) {
    const Q = sched();
    for (const e of P.ins) {
      Q.hit.set(e.m.id, 0.2 + (1.8 * ((((e.m.a + PI / 2) % TAU) + TAU) % TAU)) / TAU);
      if (e.take) Q.arr.set(e.m.id, 2.4);
    }
    return Q;
  },
  pos(e, X) { return X.k < 2.05 ? at(e.m.x, e.m.y) : toHullAt(e, inCubic((X.k - 2.05) / 0.35)); },
  under(c, X) {
    const k = X.k, R = X.R;
    if (k < 0) return;
    const a = env(k, 0, 0.2, 2.3, 2.7);
    if (!(a > 0)) return;
    ringR(c, X, 0.3 * a);
    const p = clamp01((k - 0.2) / 1.8), head = polarAt(-PI / 2 + TAU * p, R);
    if (p > 0) { arc(c, 0, 0, R, -PI / 2, -PI / 2 + TAU * p, WHITE, 0.9 * a, lw(1.6, X.z)); disc(c, head[0], head[1], 1.8, WHITE, a); }
    if (k > 2.0) circle(c, 0, 0, R, WHITE, 1 - smooth(2.0, 2.4, k), lw(2.4, X.z));
    for (const e of X.P.ins) {
      if (k < hitOf(X, e)) continue;
      const ca = Math.cos(e.m.a), sa = Math.sin(e.m.a);
      const x0 = ca * (R - 4), y0 = sa * (R - 4), x1 = ca * (R + 4), y1 = sa * (R + 4);
      if (e.take) line(c, x0, y0, x1, y1, WHITE, 0.9 * a, lw(1.3, X.z));
      else line(c, x0, y0, x1, y1, PALE, 0.7 * a, lw(1, X.z), [px(1.5, X.z), px(1.5, X.z)]);
    }
  },
};


/** Card 16: the five alpha buckets and the front of the hex sweep, kept
 *  from frame to frame and emptied after each paint. */
const CELL_BUCKETS: { x: number; y: number }[][] = [[], [], [], [], []];
const CELL_FRONT: { x: number; y: number }[] = [];

/** Card 16: one path of the hexes of `list`. */
function cellPaths(c: Ctx, list: readonly { x: number; y: number }[]): void {
  c.beginPath();
  for (const q of list) {
    for (let s = 0; s < 6; s++) {
      const an = ((60 * s - 30) * PI) / 180, x = q.x + Math.cos(an) * 5.4, y = q.y + Math.sin(an) * 5.4;
      if (s) c.lineTo(x, y); else c.moveTo(x, y);
    }
    c.closePath();
  }
}
/** Card 16 Hex Sweep: a grid of small hexes lights inside the collect
 *  radius and shrinks from the rim to the hull; each sea mine steps in from
 *  cell to cell. */
const hexSweep: SeaMineCollectLook = {
  stow: 'hex',
  prep(P, R) {
    const cells: { x: number; y: number; rho: number }[] = [], dx = Math.sqrt(3) * 6, dy = 9, N = Math.ceil((R + 6) / dy);
    for (let row = -N; row <= N; row++) {
      for (let col = -N; col <= N; col++) {
        const x = col * dx + (Math.abs(row) % 2 ? dx / 2 : 0), y = row * dy, rho = Math.hypot(x, y);
        if (rho <= R + 3) cells.push({ x, y, rho });
      }
    }
    const rf = (k: number): number => (R + 4) * (1 - smooth(0.4, 2.0, k));
    const near = (x: number, y: number): { x: number; y: number; rho: number } => {
      let b = cells[0], bd = Infinity;
      for (const q of cells) { const d = (q.x - x) ** 2 + (q.y - y) ** 2; if (d < bd) { bd = d; b = q; } }
      return b;
    };
    const Q = sched(); Q.cells = cells; Q.rf = rf; Q.near = near; Q.home = new Map();
    for (const e of P.ins) {
      Q.hit.set(e.m.id, firstK((k) => rf(k) <= e.m.d)); Q.home.set(e.m.id, near(e.m.x, e.m.y));
      if (e.take) Q.arr.set(e.m.id, firstK((k) => rf(k) <= 5));
    }
    return Q;
  },
  pos(e, X) {
    if (X.k < hitOf(X, e)) return at(e.m.x, e.m.y);
    const r = Math.min(e.m.d, X.Q.rf!(X.k)), q = X.Q.near!(Math.cos(e.m.a) * r, Math.sin(e.m.a) * r);
    return at(q.x, q.y);
  },
  under(c, X) {
    const k = X.k;
    if (k < 0) return;
    const a = env(k, 0, 0.35, 2.15, 2.6);
    if (!(a > 0)) return;
    const rf = X.Q.rf!(k), buckets = CELL_BUCKETS, front = CELL_FRONT;
    for (const b of buckets) b.length = 0;
    front.length = 0;
    for (const q of X.Q.cells!) {
      const al = clamp01((rf - q.rho) / 10 + 1);
      if (al <= 0) continue;
      if (Math.abs(q.rho - rf) < 5 && k > 0.4) front.push(q); else buckets[Math.min(4, Math.floor(al * 4.999))].push(q);
    }
    for (let i = 0; i < buckets.length; i++) {
      const b = buckets[i];
      if (!b.length) continue;
      cellPaths(c, b); Fl(c, X.ownCol, (0.035 * a * (i + 1)) / 5); S(c, X.ownCol, (0.3 * a * (i + 1)) / 5, lw(0.6, X.z));
    }
    if (front.length) { cellPaths(c, front); S(c, WHITE, 0.45 * a, lw(0.7, X.z)); }
    for (const b of buckets) b.length = 0;
    front.length = 0;
    for (const e of X.P.lefts) {
      if (k < hitOf(X, e)) continue;
      const q = X.Q.home!.get(e.m.id)!;
      hexPath(c, q.x, q.y, 5.4); Fl(c, X.ownCol, 0.08 * a); S(c, WHITE, 0.8 * a, lw(0.8, X.z), [px(1.5, X.z), px(1.5, X.z)]);
    }
  },
};

/** Card 19 Ledger Tally: no sea mine moves; each one that comes aboard fades
 *  where it lies, and its cargo figure floats up to the stow figure. */
const ledgerTally: SeaMineCollectLook = {
  stow: 'num',
  prep(P) {
    const Q = sched(); Q.tj = new Map();
    for (const e of P.ins) {
      if (!e.take) { Q.hit.set(e.m.id, 0.5); continue; }
      const tj = 0.45 + 0.3 * e.j;
      Q.tj.set(e.m.id, tj); Q.hit.set(e.m.id, tj); Q.arr.set(e.m.id, tj + 0.85);
    }
    return Q;
  },
  mine(c, e, p, q, X) {
    const k = X.k, tj = X.Q.tj!.get(e.m.id) ?? 0, u = clamp01((k - tj - 0.1) / 0.4);
    collectMine(c, X, e.m, p[0], p[1], q, 1 - smooth(tj + 0.15, tj + 0.5, k), 1);
    if (u > 0 && u < 1) circle(c, p[0], p[1], lerp(10, 3, u), WHITE, 0.8 * (1 - u), lw(1, X.z), [px(2, X.z), px(2, X.z)]);
  },
  left(c, e, X) {
    const k = X.k, h = hitOf(X, e);
    collectMine(c, X, e.m, e.m.x + jitter(k, h), e.m.y, 0, 1, 1);
    if (k >= h) struckFig(c, String(e.m.cargo), e.m.x + 6 + px(3, X.z), e.m.y - 6 - px(4, X.z), smooth(h, h + 0.2, k), X);
  },
  under(c, X) {
    const k = X.k;
    if (k < 0) return;
    if (k < 0.6) circle(c, 0, 0, X.R, WHITE, 0.9 * (1 - k / 0.6), lw(1.6, X.z));
    ringR(c, X, 0.25 * env(k, 0.3, 0.6, 2.4, 2.8));
  },
  over(c, X) {
    const k = X.k;
    if (k < 0) return;
    for (const e of X.P.takes) {
      const tj = X.Q.tj!.get(e.m.id) ?? 0, arr = arrOf(X, e);
      if (k < tj + 0.2 || k >= arr) continue;
      const u = smooth(tj + 0.2, arr, k);
      const fx = lerp(e.m.x, 0, u), fy = lerp(e.m.y - 7, numY(X.z), u);
      upright(c, X.head, fx, fy, () => word(c, WORDS.plus({ n: e.m.cargo }), fx, fy, 'note', X, WHITE, 1 - 0.4 * u, 'center'));
    }
  },
};

/** Card 20 Disarm First: a white stroke strikes each hostile sea mine and
 *  turns it own; own sea mines blink; then all of them snap in at once. */
const disarmFirst: SeaMineCollectLook = {
  stow: 'pips',
  prep(P) {
    const Q = sched();
    let f = 0;
    for (const e of P.ins) {
      if (!e.take) { Q.hit.set(e.m.id, 1.05); continue; }
      Q.arr.set(e.m.id, 1.8);
      if (e.m.side === 'foe') { const d = 0.4 + 0.2 * f; Q.dis.set(e.m.id, d); Q.hit.set(e.m.id, d); f++; } else Q.hit.set(e.m.id, 0.4);
    }
    return Q;
  },
  pos(e, X) { return X.k < 1.35 ? at(e.m.x, e.m.y) : toHullAt(e, inCubic((X.k - 1.35) / 0.45)); },
  under(c, X) { if (X.k >= 0) circle(c, 0, 0, X.R, WHITE, 0.6 * env(X.k, 0.05, 0.3, 1.35, 1.6), lw(1.2, X.z)); },
  over(c, X) {
    const k = X.k;
    if (k < 0) return;
    for (const e of X.P.takes) {
      const m = e.m;
      if (m.side === 'foe') {
        const d = X.Q.dis.get(m.id) ?? 0, d0 = d - 0.18;
        if (k >= d0 && k < 1.35) {
          const f = smooth(d0, d, k), a = 1 - smooth(d + 0.4, d + 0.7, k), r = 6.5 * ts(X.z);
          line(c, m.x - r, m.y + r, m.x - r + 2 * r * f, m.y + r - 2 * r * f, WHITE, 0.95 * a, lw(1.5, X.z));
        }
      } else flashRing(c, m.x, m.y, (k - 0.4) / 0.35, X.z, 5, 8.5);
      if (k >= 1.35 && k < 1.8) {
        const p = toHullAt(e, inCubic((k - 1.35) / 0.45)), ux = Math.cos(m.a) * 7, uy = Math.sin(m.a) * 7;
        line(c, p[0], p[1], p[0] + ux, p[1] + uy, WHITE, 0.55, lw(1, X.z));
      }
    }
  },
};

/** The look of each id. The record is typed by the list, so a look the
 *  list gains without its card is a type error. */
export const SEA_MINE_COLLECT_LOOKS: Record<SeaMineCollectStyle, SeaMineCollectLook> = {
  'ping-and-pull': pingAndPull,
  'closing-ring': closingRing,
  'tractor-beams': tractorBeams,
  'keel-squeeze': keelSqueeze,
  'clock-rim': clockRim,
  'hex-sweep': hexSweep,
  'ledger-tally': ledgerTally,
  'disarm-first': disarmFirst,
};

/**
 * Paints one sea mine collect in one look, as the own view of round 073
 * draws it: the look under the field, each sea mine of the plan where the
 * look has it (aboard ones vanish at their arrival, and a hostile one turns
 * own at its disarm), the arrival flash at the stern, the look over the
 * field, and the stow display. The Sapper itself is the fleet layer's.
 */
export function paintSeaMineCollect(c: Ctx, style: SeaMineCollectStyle, X: CollectScene): void {
  const look = SEA_MINE_COLLECT_LOOKS[style], k = X.k;
  look.under?.(c, X);
  for (const e of X.P.ins) {
    const m = e.m;
    if (k < 0) { collectMine(c, X, m, m.x, m.y, 0, 1, 1); continue; }
    if (!e.take) { (look.left ?? defaultLeft)(c, e, X); continue; }
    if (k >= arrOf(X, e)) continue;
    const dis = X.Q.dis.get(m.id) ?? hitOf(X, e);
    const q = m.side === 'foe' ? smooth(dis, dis + 0.25, k) : 0;
    const p = look.pos ? look.pos(e, X) : at(m.x, m.y);
    if (look.mine) look.mine(c, e, p, q, X); else collectMine(c, X, m, p[0], p[1], q, 1, 1);
    if (m.side === 'foe') flashRing(c, p[0], p[1], (k - dis) / 0.35, X.z);
  }
  if (k >= 0) for (const e of X.P.takes) flashRing(c, -2.5, 0, (k - arrOf(X, e)) / 0.3, X.z, 2, 7);
  look.over?.(c, X);
  if (X.timer === undefined || look.stow !== 'num') STOW_DISPLAYS[look.stow](c, X, stowState(X));
  if (X.timer !== undefined) paintTimer(c, X, X.timer, look.stow !== 'num' && look.stow !== 'ring');
}

/**
 * The hostile view of a sea mine collect (round 073): the commander sees
 * none of the other side's sea mines, and each of its own that the Sapper
 * takes fades where it lies inside a shrinking dashed ring.
 */
export function paintSeaMineCollectHostile(c: Ctx, X: CollectScene, foeMines: readonly FieldMine[]): void {
  const k = X.k;
  for (const m of foeMines) {
    const e = X.P.byId.get(m.id);
    if (e && e.take && k >= 0 && k >= arrOf(X, e)) {
      const u = (k - arrOf(X, e)) / 0.45;
      if (u < 1) {
        collectMine(c, X, m, m.x, m.y, 0, 1 - u, 1);
        circle(c, m.x, m.y, lerp(12, 4, u), X.foeCol, 0.85 * (1 - u), lw(1, X.z), [px(2, X.z), px(2, X.z)]);
      }
      continue;
    }
    collectMine(c, X, m, m.x, m.y, 0, 1, 1);
  }
}

// =================================================================================
// Round 074: a Sapper collects deployables
// =================================================================================

/** The state of one deployable at the collect. */
export type ItemState = 'aboard' | 'noroom' | 'hostile' | 'idle';

/** One deployable of the field, in the frame of the Sapper at rest. */
export interface FieldItem {
  id: string;
  kind: DeployableKind;
  side: 'own' | 'foe';
  /** The anchor point, and the bearing of a line kind. */
  x: number; y: number; ang: number;
  cargo: number;
}

/** A field item with the plan of the take. */
export interface PlannedItem extends FieldItem {
  d: number; bear: number; inside: boolean; state: ItemState;
  /** The place in the stow order, and the stow before and after it. */
  rank: number; s0: number; s1: number;
}

/** The take of one deployable collect. */
export interface DeployTakePlan {
  R: number;
  items: PlannedItem[];
  /** The stow after the collect. */
  stow: number;
  aboard: PlannedItem[];
  stay: PlannedItem[];
  noroom: PlannedItem[];
  n: number;
}

const byItemDistance = (a: PlannedItem, b: PlannedItem): number => a.d - b.d;

/**
 * The take of the rule (R2635): every own deployable inside the radius,
 * nearest first; it comes aboard while the stow has room for its cargo. A
 * hostile deployable cannot come aboard (§11.1): a gun must sink it.
 */
export function planDeployableTake(field: readonly FieldItem[], R: number, stow0: number, cap: number): DeployTakePlan {
  const items: PlannedItem[] = field.map((b) => {
    const d = Math.hypot(b.x, b.y);
    return { ...b, d, bear: Math.atan2(b.y, b.x), inside: d <= R, state: 'idle' as ItemState, rank: -1, s0: 0, s1: 0 };
  });
  // The own items inside, nearest first: the sort is stable, so a tie
  // keeps the order of the field. They come aboard in this order, so the
  // items aboard stand in the order of their rank.
  const aboard: PlannedItem[] = [];
  for (const it of items) if (it.side === 'own' && it.inside) aboard.push(it);
  aboard.sort(byItemDistance);
  let stow = stow0, rank = 0, n = 0;
  for (const it of aboard) {
    if (stow + it.cargo <= cap) { it.state = 'aboard'; it.rank = rank++; it.s0 = stow; stow += it.cargo; it.s1 = stow; aboard[n++] = it; }
    else it.state = 'noroom';
  }
  aboard.length = n;
  const stay: PlannedItem[] = [], noroom: PlannedItem[] = [];
  for (const it of items) {
    if (it.side === 'foe' && it.inside) it.state = 'hostile';
    if (it.state === 'hostile' || it.state === 'noroom') stay.push(it);
    if (it.state === 'noroom') noroom.push(it);
  }
  return { R, items, stow, aboard, stay, noroom, n: aboard.length };
}

/** Everything a deployable collect look reads. The Sapper is at rest at the
 *  origin, bow to +x, and its stow square stands at `sp`. */
export interface DeployCollectScene {
  t: number;
  R: number;
  p: DeployTakePlan;
  own: boolean;
  /** The moment the Sapper stops, the collect window, and the end of the
   *  last item's window. */
  tStop: number; tC0: number; tC1: number; tEnd: number;
  /** The stow point on the hull. */
  sp: Pt;
  /** The length of a line kind, and the radius of the Cheval. */
  lenSeine: number; lenPalisade: number; Rc: number;
  cap: number;
  ownCol: number; foeCol: number;
  z: number; face: string;
  /** The turn of the frame on the map, as `CollectScene.head` (R2711). */
  head?: number;
  /** The mark of the `deployable` field, which stands in place of the
   *  placeholder (R2657): it paints the whole kind at the origin, a line
   *  along +x, at alpha `a`. `markAt` turns it and scales it to the fold. */
  mark?: (c: Ctx, it: FieldItem, ink: number, a: number) => void;
}

/** One deployable collect look: its window of each item, and the hooks its
 *  card fills. */
export interface DeployableCollectLook {
  /** The window of an item. It answers `span`, the one kept window of the
   *  module, so the caller reads it before the next ask. */
  win?(it: PlannedItem, o: DeployCollectScene): readonly [number, number];
  item(c: Ctx, it: PlannedItem, u: number, o: DeployCollectScene): void;
  wait?(c: Ctx, it: PlannedItem, o: DeployCollectScene): void;
  after?(c: Ctx, it: PlannedItem, k: number, o: DeployCollectScene): void;
  radius?(c: Ctx, o: DeployCollectScene): void;
  stay?(c: Ctx, it: PlannedItem, o: DeployCollectScene): void;
  fx?(c: Ctx, o: DeployCollectScene): void;
}

const isLine = (it: FieldItem): boolean => it.kind === 'seine' || it.kind === 'palisade';
const isPoint = (it: FieldItem): boolean => it.kind === 'picket' || it.kind === 'lane' || it.kind === 'decoy';
const colOf = (it: FieldItem, o: DeployCollectScene): number => (it.side === 'own' ? o.ownCol : o.foeCol);
const lenOf = (it: FieldItem, o: DeployCollectScene): number => (it.kind === 'seine' ? o.lenSeine : o.lenPalisade);

// ---- the placeholder marks of rounds 070 to 074 ---------------------------------

function seineLine(c: Ctx, o: DeployCollectScene, x0: number, x1: number, ink: number, a: number, off = 0): void {
  if (!(a > 0) || x1 - x0 < 0.3) return;
  if (x1 - x0 < 16) {
    line(c, x0, 0, x1, 0, ink, 0.9 * a, lw(1.2, o.z)); disc(c, x0, 0, 1.4, ink, a); disc(c, x1, 0, 1.4, ink, a);
    return;
  }
  line(c, x0, 0, x1, 0, ink, 0.9 * a, lw(1.2, o.z), [4, 2.5]);
  for (let f = -o.lenSeine / 2; f <= o.lenSeine / 2; f += 10) {
    const x = f + off;
    if (x >= x0 - 0.01 && x <= x1 + 0.01) disc(c, x, 0, 1.5, ink, a);
  }
}
function palLine(c: Ctx, o: DeployCollectScene, x0: number, x1: number, ink: number, a: number): void {
  if (!(a > 0) || x1 - x0 < 0.3) return;
  c.beginPath(); c.moveTo(x0, 0); c.lineTo(x1, 0); c.lineWidth = Math.max(3, px(1.2, o.z)); c.lineCap = 'butt';
  c.strokeStyle = rgba(ink, a); c.setLineDash([]); c.stroke();
}
function lineMark(c: Ctx, o: DeployCollectScene, kind: DeployableKind, x: number, y: number, ang: number, L: number, ink: number, a: number): void {
  c.save(); c.translate(x, y); c.rotate(ang);
  if (kind === 'seine') seineLine(c, o, -L / 2, L / 2, ink, a); else palLine(c, o, -L / 2, L / 2, ink, a);
  c.restore();
}
/** The Cheval: a zigzag ellipse with a low fill. */
function chevalShape(c: Ctx, o: DeployCollectScene, x: number, y: number, rx: number, ry: number, r: number, ink: number, a: number): void {
  if (!(a > 0) || rx < 0.4 || ry < 0.4) return;
  const R = Math.max(rx, ry), n = Math.max(8, 2 * Math.round((TAU * R) / 13)), dep = Math.min(3, R * 0.22);
  c.save(); c.translate(x, y); c.rotate(r); c.beginPath();
  for (let i = 0; i < n; i++) {
    const th = (i / n) * TAU, f = i % 2 ? (R - dep) / R : 1, qx = Math.cos(th) * rx * f, qy = Math.sin(th) * ry * f;
    if (i) c.lineTo(qx, qy); else c.moveTo(qx, qy);
  }
  c.closePath(); Fl(c, ink, 0.09 * a); S(c, ink, 0.85 * a, lw(1, o.z)); c.restore();
}
function pointMark(c: Ctx, o: DeployCollectScene, kind: DeployableKind, x: number, y: number, s: number, ink: number, a: number, sy?: number): void {
  if (!(a > 0) || !(s > 0.02)) return;
  c.save(); c.translate(x, y);
  if (sy !== undefined) c.scale(1, Math.max(0.06, sy));
  if (kind === 'picket') { circle(c, 0, 0, 4 * s, ink, a, lw(1.2, o.z)); disc(c, 0, 0, 1.3 * s, ink, a); }
  else if (kind === 'lane') { path4(c, 0, -3 * s, 3 * s, 0, 0, 3 * s, -3 * s, 0); Fl(c, ink, a); }
  else { path3(c, 0, -3 * s, 3 * s, 2.2 * s, -3 * s, 2.2 * s); S(c, ink, a, lw(1.1, o.z)); }
  c.restore();
}
/** The pack a deployable folds into for the stow. */
const PACK = { seine: 8, palisade: 9, cheval: 4 } as const;
/** One deployable at (x, y) with the fold share `f`: 0 laid out, 1 packed. */
function markAt(
  c: Ctx, o: DeployCollectScene, it: FieldItem, x: number, y: number, f: number, a: number, s = 1,
  ang: number | null = null, inkOver?: number,
): void {
  const ink = inkOver ?? colOf(it, o), r = ang ?? it.ang;
  if (o.mark) {
    if (!(a > 0) || !(s > 0.02)) return;
    c.save(); c.translate(x, y);
    if (isLine(it)) {
      const L = lenOf(it, o);
      c.rotate(r); c.scale((lerp(L, PACK[it.kind as 'seine' | 'palisade'], f) * s) / Math.max(1, L), s);
    } else if (it.kind === 'cheval') { const k = (lerp(o.Rc, PACK.cheval, f) * s) / Math.max(1, o.Rc); c.scale(k, k); }
    else c.scale(s, s);
    o.mark(c, it, ink, a);
    c.restore();
    return;
  }
  if (isLine(it)) lineMark(c, o, it.kind, x, y, r, lerp(lenOf(it, o), PACK[it.kind as 'seine' | 'palisade'], f) * s, ink, a);
  else if (it.kind === 'cheval') { const rr = lerp(o.Rc, PACK.cheval, f) * s; chevalShape(c, o, x, y, rr, rr, 0, ink, a); }
  else pointMark(c, o, it.kind, x, y, s, ink, a);
}
const still = (c: Ctx, o: DeployCollectScene, it: FieldItem): void => markAt(c, o, it, it.x, it.y, 0, 1);
/** The point an item runs to the stow through, `m` of the way. One point,
 *  kept from frame to frame: the caller reads it before the next ask. */
const RUN: [number, number] = [0, 0];
const runTo = (o: DeployCollectScene, it: FieldItem, m: number): Pt => {
  RUN[0] = lerp(it.x, o.sp[0], m); RUN[1] = lerp(it.y, o.sp[1], m); return RUN;
};
function stowFlash(c: Ctx, o: DeployCollectScene, k: number): void {
  if (k < 0 || k > 0.35) return;
  const q = k / 0.35;
  circle(c, o.sp[0], o.sp[1], 3 + 6 * q, WHITE, 0.85 * (1 - q), lw(1, o.z));
}

// ---- the tells every card may fall back on --------------------------------------

/** The radius opens from the r19 ring when the Sapper stops, and fades
 *  after the collect. */
function defaultRadius(c: Ctx, o: DeployCollectScene): void {
  const k = o.t - o.tStop;
  if (k < 0) return;
  const r = lerp(19, o.R, outCubic(k / 0.5)), a = fade(o.t, o.tEnd + 0.2, o.tEnd + 0.9);
  disc(c, 0, 0, r, o.ownCol, 0.035 * a);
  circle(c, 0, 0, r, o.ownCol, 0.6 * a, lw(1.1, o.z), [px(4, o.z), px(3, o.z)]);
}
/** Four target corners: a gun must sink a hostile deployable. */
function targetCorners(c: Ctx, o: DeployCollectScene, x: number, y: number, r: number, ink: number, a: number): void {
  const q = r * 0.45;
  for (let i = 0; i < 4; i++) {
    const sx = CORNER_X[i], sy = CORNER_Y[i];
    path3(c, x + sx * r, y + sy * (r - q), x + sx * r, y + sy * r, x + sx * (r - q), y + sy * r, false); S(c, ink, a, lw(1.2, o.z));
  }
}
/** The stow sign struck through: no room. */
function noRoom(c: Ctx, o: DeployCollectScene, x: number, y: number, ink: number, a: number): void {
  const s = 3;
  c.beginPath(); c.rect(x - s, y - s, 2 * s, 2 * s); S(c, ink, a, lw(1.1, o.z));
  line(c, x - s - 1.6, y + s + 1.6, x + s + 1.6, y - s - 1.6, ink, a, lw(1.1, o.z));
}
/** The no-room tell of an item: one point, kept as `RUN` is. */
const NO_ROOM: [number, number] = [0, 0];
const noRoomAt = (it: FieldItem): Pt => {
  if (isPoint(it)) { NO_ROOM[0] = it.x + 9; NO_ROOM[1] = it.y - 9; } else { NO_ROOM[0] = it.x; NO_ROOM[1] = it.y - 12; }
  return NO_ROOM;
};
function defaultStay(c: Ctx, it: PlannedItem, o: DeployCollectScene): void {
  const a = smooth(o.tC0 - 0.2, o.tC0 + 0.2, o.t);
  if (!(a > 0)) return;
  if (it.state === 'hostile') targetCorners(c, o, it.x, it.y, 8, WHITE, 0.9 * a);
  else { const q = noRoomAt(it); noRoom(c, o, q[0], q[1], WHITE, 0.9 * a); }
}
/** The one window a `win` hook answers, kept from frame to frame. */
const WIN: [number, number] = [0, 0];
const span = (t0: number, t1: number): readonly [number, number] => { WIN[0] = t0; WIN[1] = t1; return WIN; };
function winOf(look: DeployableCollectLook, it: PlannedItem, o: DeployCollectScene): readonly [number, number] {
  return look.win ? look.win(it, o) : span(o.tC0, o.tC1);
}
/** The cargo aboard at the time of the scene, each item counted as it
 *  reaches the stow. */
function stowAt(look: DeployableCollectLook, o: DeployCollectScene): number {
  let s = 0;
  for (const it of o.p.aboard) { const w = winOf(look, it, o); s += it.cargo * smooth(w[1] - 0.25, w[1], o.t); }
  return s;
}
/** The half height of the box of a tag in its step, map px: 0.625 of the
 *  size of the word, as the 5 px of the 8 px word of card 10 (R3249). */
const tagHalf = (step: ChartStep, z: number): number => 0.625 * bandPx('mono', step, z);
/** A tag on the map: a dark box with a figure (a note) or a warning (a
 *  label). The word stands in the world, in the band of its step, and its
 *  box follows the word (R3249). */
function tag(
  c: Ctx, o: DeployCollectScene, x: number, y: number, s: string, step: ChartStep, a: number, ink: number,
  strike = false,
): void {
  const wp = bandPx('mono', step, o.z);
  if (!(a > 0) || !(wp > 0)) return;
  // The tag stands level at every heading of the Sapper (R2711).
  upright(c, o.head, x, y, () => {
    c.font = wordFont('mono', wp, o.face);
    const tw = c.measureText(s).width, ph = 0.625 * wp, pw = 0.375 * wp;
    c.beginPath(); c.rect(x - tw / 2 - pw, y - ph - 0.5 / o.z, tw + 2 * pw, 2 * ph + 1 / o.z); Fl(c, DEEP, 0.85 * a); S(c, ink, 0.7 * a, px(1, o.z));
    text(c, s, x, y + 0.3 / o.z, c.font, ink, a);
    if (strike) line(c, x - tw / 2 - pw, y, x + tw / 2 + pw, y, ink, 0.9 * a, px(1, o.z));
  });
}
/** Card 10: where the tag of an item stands, off the hull ring. */
/** The tag of an item: one point, kept as `RUN` is. */
const TAG: [number, number] = [0, 0];
function tagAt(it: FieldItem): Pt {
  const upY = it.y - 12;
  TAG[0] = it.x; TAG[1] = Math.hypot(it.x, upY) < 24 ? it.y + 13 : upY;
  return TAG;
}
/** Map text on a dark halo, in the band of its step (R3249). */
function halo(c: Ctx, o: DeployCollectScene, s: string, x: number, y: number, step: ChartStep, ink: number, a: number): void {
  const wp = bandPx('mono', step, o.z);
  if (!(a > 0) || !(wp > 0)) return;
  upright(c, o.head, x, y, () => {
    c.font = wordFont('mono', wp, o.face); c.textAlign = 'center'; c.textBaseline = 'middle';
    c.lineWidth = 3 / o.z; c.lineJoin = 'round'; c.strokeStyle = rgba(DEEP, 0.85 * a); c.strokeText(s, x, y);
    c.fillStyle = rgba(ink, a); c.fillText(s, x, y);
  });
}

// ---- the helpers of single cards ------------------------------------------------

/** Card 04: the ring closes in 3.6 s. */
const RING_D = 3.6;
const closeR = (o: DeployCollectScene): number => lerp(o.R, 19, inOut((o.t - o.tC0) / RING_D));
function closeT(o: DeployCollectScene, d: number): number {
  let lo = 0, hi = 1;
  for (let i = 0; i < 26; i++) { const mid = (lo + hi) / 2; if (lerp(o.R, 19, inOut(mid)) > d) lo = mid; else hi = mid; }
  return o.tC0 + RING_D * lo;
}

/** Card 05: the dashed outline of a deployable, drained to `share`. */
function blueprintOf(c: Ctx, o: DeployCollectScene, it: PlannedItem, share: number, a: number): void {
  if (!(a > 0) || share <= 0.01) return;
  const dash = [px(2, o.z), px(2, o.z)];
  c.save(); c.translate(it.x, it.y);
  if (isLine(it)) {
    const hl = (lenOf(it, o) / 2) * share, hw = it.kind === 'seine' ? 3 : 3.5;
    c.rotate(it.ang); c.beginPath(); c.rect(-hl, -hw, 2 * hl, 2 * hw); S(c, PALE, a, lw(1, o.z), dash);
    line(c, -hl, 0, hl, 0, PALE, 0.5 * a, lw(0.8, o.z), dash);
  } else if (it.kind === 'cheval') {
    const n = 30, m = Math.ceil(n * share), b0 = it.bear + PI;
    c.beginPath();
    for (let i = 0; i <= m; i++) {
      const th = b0 + Math.min(i / n, share) * TAU, r = i % 2 ? o.Rc - 3 : o.Rc, qx = Math.cos(th) * r, qy = Math.sin(th) * r;
      if (i) c.lineTo(qx, qy); else c.moveTo(qx, qy);
    }
    S(c, PALE, a, lw(1, o.z), dash); circle(c, 0, 0, 2, PALE, 0.6 * a, lw(0.8, o.z));
  } else {
    const s = 0.35 + 0.65 * share;
    if (it.kind === 'picket') {
      arc(c, 0, 0, 4.5, -PI / 2, -PI / 2 + TAU * share, PALE, a, lw(1, o.z)); circle(c, 0, 0, 1.4 * s, PALE, a, lw(0.8, o.z));
    } else { path4(c, 0, -4 * s, 4 * s, 0, 0, 4 * s, -4 * s, 0); S(c, PALE, a, lw(1, o.z), dash); }
  }
  c.restore();
}

/** Card 06: a line deployable folded in a zigzag, ten px a leaf. */
function concertina(c: Ctx, o: DeployCollectScene, it: PlannedItem, x: number, y: number, f: number, a: number, s: number): void {
  const N = it.kind === 'seine' ? 8 : 6, seg = 10 * s, phi = f * 1.32, ext = N * seg * Math.cos(phi), hgt = seg * Math.sin(phi);
  const ink = colOf(it, o);
  c.save(); c.translate(x, y); c.rotate(it.ang);
  // The path of the leaves, then a float at each fold: the points are
  // computed twice and held in no array.
  c.beginPath();
  for (let i = 0; i <= N; i++) {
    const qx = -ext / 2 + i * seg * Math.cos(phi), qy = i % 2 ? hgt / 2 : -hgt / 2;
    if (i) c.lineTo(qx, qy); else c.moveTo(qx, qy);
  }
  if (it.kind === 'seine') {
    S(c, ink, 0.9 * a, lw(1.2, o.z), f < 0.3 ? [4, 2.5] : []);
    for (let i = 0; i <= N; i++) disc(c, -ext / 2 + i * seg * Math.cos(phi), i % 2 ? hgt / 2 : -hgt / 2, 1.5, ink, a);
  } else { c.lineWidth = 3; c.lineJoin = 'miter'; c.lineCap = 'butt'; c.strokeStyle = rgba(ink, a); c.setLineDash([]); c.stroke(); }
  c.restore();
}
/** Card 06: the Cheval as a closing fan. */
function fan(c: Ctx, o: DeployCollectScene, it: PlannedItem, x: number, y: number, f: number, a: number, s: number): void {
  const span = TAU * (1 - 0.75 * f), R = o.Rc * (1 - 0.62 * f) * s, ink = colOf(it, o);
  if (span > TAU - 0.02) { chevalShape(c, o, x, y, R, R, 0, ink, a); return; }
  const mid = Math.atan2(it.y - o.sp[1], it.x - o.sp[0]), a0 = mid - span / 2, n = Math.max(4, 2 * Math.round((span * R) / 13)), dep = Math.min(3, R * 0.22);
  c.beginPath(); c.moveTo(x, y);
  for (let i = 0; i <= n; i++) { const th = a0 + (span * i) / n, r = i % 2 ? R - dep : R; c.lineTo(x + Math.cos(th) * r, y + Math.sin(th) * r); }
  c.closePath(); Fl(c, ink, 0.12 * a); S(c, ink, 0.85 * a, lw(1, o.z));
}

/** Card 13: the chain under an anchor point and its stud. */
function gear(c: Ctx, o: DeployCollectScene, x: number, y: number, len: number, hollow: boolean, ink: number, a: number, t: number): void {
  if (!(a > 0)) return;
  const y0 = y + 3, y1 = y0 + Math.max(0, len);
  if (len > 0.3) {
    c.beginPath(); c.moveTo(x, y0); c.lineTo(x, y1); c.lineWidth = lw(1, o.z); c.strokeStyle = rgba(ink, 0.85 * a);
    c.setLineDash([2, 1.5]); c.lineDashOffset = t * 8; c.stroke(); c.setLineDash([]); c.lineDashOffset = 0;
  }
  if (hollow) circle(c, x, y1 + 1.8, 1.8, ink, a, lw(1, o.z));
  else { disc(c, x, y1 + 1.8, 1.9, ink, a); line(c, x - 2.8, y1 + 3.9, x + 2.8, y1 + 3.9, ink, a, lw(1.1, o.z)); }
}

// ---- the nine looks ------------------------------------------------------------------

/** Card 01 Straight Haul: each deployable folds, and the pack runs straight
 *  to the stow on a dotted track; the stow flashes as it takes it. */
const straightHaul: DeployableCollectLook = {
  item(c, it, u, o) {
    const f = smooth(0, 0.42, u), m = inOut(clamp01((u - 0.45) / 0.55)), q = runTo(o, it, m), a = fade(u, 0.88, 1), s = 1 - 0.5 * smooth(0.8, 1, u);
    if (o.own && u > 0.4 && u < 0.95) line(c, q[0], q[1], o.sp[0], o.sp[1], PALE, 0.45, lw(1, o.z), [px(1, o.z), px(3, o.z)]);
    if (isPoint(it)) {
      const hgt = 3 * f * (1 - smooth(0.7, 1, u));
      markAt(c, o, it, q[0] + 0.7 * hgt, q[1] + hgt, 0, 0.7 * a, s, null, DEEP);
      markAt(c, o, it, q[0], q[1] - 0.4 * hgt, 0, a, s * (1 + 0.25 * f));
    } else markAt(c, o, it, q[0], q[1], f, a, s);
  },
  after(c, _it, k, o) { stowFlash(c, o, k); },
};

/** Card 02 Winch Lines: a winch line runs to each own anchor and goes taut,
 *  and the winch draws each deployable in along its line. */
const winchLines: DeployableCollectLook = {
  item(c, it, u, o) {
    const out = smooth(0, 0.22, u), taut = smooth(0.22, 0.34, u), m = inOut(clamp01((u - 0.36) / 0.64)), q = runTo(o, it, m);
    if (o.own) {
      const ex = u < 0.36 ? lerp(o.sp[0], it.x, out) : q[0], ey = u < 0.36 ? lerp(o.sp[1], it.y, out) : q[1];
      const mx = (o.sp[0] + ex) / 2, my = (o.sp[1] + ey) / 2, L = Math.hypot(ex - o.sp[0], ey - o.sp[1]) || 1, sag = (1 - taut) * 0.14 * L;
      c.beginPath(); c.moveTo(o.sp[0], o.sp[1]); c.quadraticCurveTo(mx, my + sag, ex, ey); S(c, WHITE, 0.75 * fade(u, 0.9, 1), lw(0.9, o.z));
      disc(c, ex, ey, 1.3, WHITE, 0.95 * fade(u, 0.9, 1));
    }
    markAt(c, o, it, q[0], q[1], smooth(0.36, 0.72, u), fade(u, 0.9, 1), 1 - 0.4 * smooth(0.8, 1, u));
  },
};

/** Card 04: the start and the end of each gap of the ring, kept from frame
 *  to frame. */
const GAP0: number[] = [], GAP1: number[] = [];

/** Card 04 Closing Ring: the collect radius opens and closes on the hull;
 *  each own deployable folds into a bead where the ring reaches it, and the
 *  ring opens a gap round an item it may not take. */
const closingRingDeploy: DeployableCollectLook = {
  win(it, o) { return span(closeT(o, it.d), o.tC0 + RING_D); },
  item(c, it, _u, o) {
    const t0 = closeT(o, it.d), f = smooth(0, 0.35, o.t - t0), r = Math.min(it.d, closeR(o));
    markAt(c, o, it, Math.cos(it.bear) * r, Math.sin(it.bear) * r, f, fade(o.t, o.tC0 + RING_D - 0.3, o.tC0 + RING_D), 1 - 0.3 * f);
  },
  radius(c, o) {
    const k = o.t - o.tStop;
    if (k < 0) return;
    const r = o.t < o.tC0 ? lerp(19, o.R, outCubic(k / 0.5)) : closeR(o), a = fade(o.t, o.tC0 + RING_D, o.tC0 + RING_D + 0.5);
    disc(c, 0, 0, r, o.ownCol, 0.045 * a);
    // The gaps, sorted by their start as they come in: an insertion sort
    // is stable, as the sort of an array is.
    const g0 = GAP0, g1 = GAP1;
    let n = 0;
    for (const it of o.p.stay) {
      if (!(Math.abs(r - it.d) < 9)) continue;
      const hw = 9 / r, s0 = it.bear - hw, s1 = it.bear + hw;
      let i = n++;
      for (; i > 0 && g0[i - 1] > s0; i--) { g0[i] = g0[i - 1]; g1[i] = g1[i - 1]; }
      g0[i] = s0; g1[i] = s1;
    }
    if (!n) circle(c, 0, 0, r, o.ownCol, 0.85 * a, lw(1.4, o.z));
    else {
      for (let i = 0; i < n; i++) {
        const nx = g0[(i + 1) % n];
        arc(c, 0, 0, r, g1[i], nx + (i + 1 === n ? TAU : 0), o.ownCol, 0.85 * a, lw(1.4, o.z));
      }
    }
    g0.length = 0; g1.length = 0;
    if (o.t >= o.tC0 + RING_D) circle(c, 0, 0, 19 + 6 * smooth(o.tC0 + RING_D, o.tC0 + RING_D + 0.5, o.t), WHITE, 0.7 * a, lw(1.2, o.z));
  },
  stay(c, it, o) {
    if (o.t > o.tC0 && closeR(o) < it.d - 4) circle(c, it.x, it.y, 4.5, WHITE, 0.85, lw(1, o.z), [px(2, o.z), px(2, o.z)]);
  },
};

/** Card 05 Blueprint Drain: each own deployable turns back into its plan,
 *  and the outline drains to its anchor as a white level rises in the stow
 *  square. Nothing travels. */
const blueprintDrain: DeployableCollectLook = {
  item(c, it, u, o) {
    if (!o.own) { markAt(c, o, it, it.x, it.y, smooth(0.2, 0.9, u), fade(u, 0.3, 0.9)); return; }
    const b = smooth(0, 0.25, u), dr = smooth(0.28, 0.9, u);
    if (b < 1) markAt(c, o, it, it.x, it.y, 0, 1 - b);
    blueprintOf(c, o, it, 1 - dr, 0.95 * b * fade(u, 0.9, 1));
  },
  stay(c, it, o) {
    const k = o.t - o.tC0;
    if (it.state === 'noroom' && k > 0 && k < 0.9) blueprintOf(c, o, it, 1, 0.9 * Math.abs(Math.sin(k * PI * 3.3)));
    defaultStay(c, it, o);
  },
  fx(c, o) {
    if (!o.own) return;
    const share = stowAt(blueprintDrain, o) / o.cap;
    if (share <= 0) return;
    c.fillStyle = rgba(WHITE, 0.95);
    c.fillRect(o.sp[0] - 2 + 0.5, o.sp[1] + 2 - 4 * share, 3, 4 * share - 0.5);
  },
};

/** Card 06 Concertina: each deployable folds the way a field kit folds,
 *  then the packs slide to the stow on a short curve. */
const concertinaLook: DeployableCollectLook = {
  item(c, it, u, o) {
    const f = smooth(0, 0.5, u), m = inOut(clamp01((u - 0.55) / 0.45)), a = fade(u, 0.9, 1), q = runTo(o, it, m);
    const nx = -(o.sp[1] - it.y), ny = o.sp[0] - it.x, nl = Math.hypot(nx, ny) || 1, bow = Math.sin(PI * m) * 8;
    const x = q[0] + (nx / nl) * bow, y = q[1] + (ny / nl) * bow, s = 1 - 0.5 * smooth(0.85, 1, u);
    if (isLine(it)) concertina(c, o, it, x, y, f, a, s);
    else if (it.kind === 'cheval') fan(c, o, it, x, y, f, a, s);
    else pointMark(c, o, it.kind, x, y, s, colOf(it, o), a, 1 - 0.85 * f);
  },
};

/** Card 10 Tally Tags: the stow rule in words; every deployable inside the
 *  radius gets a tag, and the items hop aboard one by one in tag order. */
const tallyTags: DeployableCollectLook = {
  win(it, o) { const t0 = o.tC0 + 0.5 + it.rank * 0.8; return span(t0, t0 + 0.6); },
  item(c, it, u, o) {
    const f = smooth(0, 0.3, u), m = inOut(clamp01((u - 0.25) / 0.75)), q = runTo(o, it, m), a = fade(u, 0.85, 1);
    markAt(c, o, it, q[0], q[1], f, a, 1 - 0.5 * m);
    if (o.own) {
      const tp = tagAt(it);
      tag(c, o, lerp(tp[0], o.sp[0], m), lerp(tp[1], o.sp[1] - 12, m), WORDS.tally({ rank: it.rank + 1, cargo: it.cargo }), 'note', a, WHITE);
    }
  },
  wait(c, it, o) {
    still(c, o, it);
    if (o.own) {
      const tp = tagAt(it);
      tag(c, o, tp[0], tp[1], WORDS.tally({ rank: it.rank + 1, cargo: it.cargo }), 'note', smooth(o.tStop + 0.2, o.tStop + 0.5, o.t), WHITE);
    }
  },
  stay(c, it, o) {
    const a = smooth(o.tStop + 0.2, o.tStop + 0.5, o.t), tp = tagAt(it);
    if (it.state === 'hostile') tag(c, o, tp[0], tp[1], WORDS.hostile, 'label', a, o.foeCol);
    else {
      // The struck cargo stands over NO ROOM, the two boxes 1 screen px apart.
      const up = tagHalf('label', o.z) + tagHalf('note', o.z) + 2 / o.z;
      tag(c, o, tp[0], tp[1] - up, String(it.cargo), 'note', a, WHITE, true); tag(c, o, tp[0], tp[1], WORDS.no_room, 'label', a, WHITE);
    }
  },
};

/** Card 13 Weigh Anchors: each deployable shows its chain and its anchor
 *  stud; the own anchors weigh, and the freed deployable drifts to the stow.
 *  The hostile anchor stays moored. */
const weighAnchors: DeployableCollectLook = {
  item(c, it, u, o) {
    const ch = 9 * (1 - smooth(0, 0.35, u)), bob = 1.5 * smooth(0.1, 0.35, u), m = inOut(smooth(0.38, 1, u));
    if (o.own && u < 0.4) gear(c, o, it.x, it.y - bob, ch, u > 0.05, colOf(it, o), fade(u, 0.3, 0.4), o.t);
    const q = runTo(o, it, m), nx = -(o.sp[1] - it.y), ny = o.sp[0] - it.x, nl = Math.hypot(nx, ny) || 1, sw = Math.sin(TAU * m) * 5 * (1 - m);
    markAt(c, o, it, q[0] + (nx / nl) * sw, q[1] + (ny / nl) * sw - bob * (1 - m), smooth(0.38, 0.7, u), fade(u, 0.9, 1), 1 - 0.4 * smooth(0.7, 1, u));
  },
  wait(c, it, o) {
    still(c, o, it);
    if (o.own) gear(c, o, it.x, it.y, 9 * smooth(o.tStop + 0.2, o.tStop + 0.6, o.t), false, colOf(it, o), smooth(o.tStop + 0.2, o.tStop + 0.4, o.t), 0);
  },
  stay(c, it, o) {
    gear(c, o, it.x, it.y, 9 * smooth(o.tStop + 0.2, o.tStop + 0.6, o.t), false, colOf(it, o), smooth(o.tStop + 0.2, o.tStop + 0.4, o.t), 0);
    defaultStay(c, it, o);
  },
};

/** Card 15 Timer Ring: one collect is one event; a bright arc runs round the
 *  radius, and when it closes every own deployable snaps aboard at once. */
const timerRing: DeployableCollectLook = {
  win(_it, o) { return span(o.tC1 - 0.3, o.tC1); },
  wait(c, it, o) {
    const k = o.t - o.tC0;
    markAt(c, o, it, it.x, it.y, 0, k > 0 ? 0.72 + 0.28 * Math.cos((TAU * k) / 0.9) : 1);
  },
  item(c, it, u, o) {
    markAt(c, o, it, it.x, it.y, u, 1 - u * u, 1 - 0.5 * u);
    disc(c, it.x, it.y, 5 * (1 - u), WHITE, 0.9 * (1 - u));
  },
  radius(c, o) {
    const k = o.t - o.tStop;
    if (k < 0) return;
    const r = lerp(19, o.R, outCubic(k / 0.5)), a = fade(o.t, o.tC1 + 0.1, o.tC1 + 0.6), e = clamp01((o.t - o.tC0) / (o.tC1 - 0.3 - o.tC0));
    disc(c, 0, 0, r, o.ownCol, 0.03 * a); circle(c, 0, 0, r, o.ownCol, 0.3 * a, lw(1, o.z));
    if (o.t > o.tC0 && e < 1) {
      arc(c, 0, 0, r, 0, TAU * e, o.ownCol, 0.95, lw(1.8, o.z));
      disc(c, Math.cos(TAU * e) * r, Math.sin(TAU * e) * r, 2, WHITE, 0.95);
    }
    if (e >= 1) circle(c, 0, 0, r, WHITE, 0.8 * a, lw(1.6, o.z));
  },
  fx(c, o) {
    const k = o.t - o.tC1;
    if (o.p.n && k >= 0 && k < 0.5) circle(c, 0, 0, 19 + 8 * (k / 0.5), WHITE, 0.85 * (1 - k / 0.5), lw(1.4, o.z));
  },
};

/** Card 20 Quiet Take: the least that reads; every own deployable inside
 *  the radius fades where it lies, and one line rises over the Sapper with
 *  the count taken and the stow. */
const quietTake: DeployableCollectLook = {
  win(_it, o) { return span(o.tC0, o.tC0 + 0.6); },
  item(c, it, u, o) { markAt(c, o, it, it.x, it.y, 0, 1 - u); },
  fx(c, o) {
    const k = o.t - (o.tC0 + 0.6);
    if (!o.own || k < 0 || k > 2.2 || !o.p.n) return;
    halo(c, o, WORDS.quiet_take({ n: o.p.n, stow: o.p.stow, cap: o.cap }), 0, -27 - 6 * outCubic(k / 2.2), 'note', WHITE,
      smooth(0, 0.15, k) * fade(k, 1.6, 2.2));
  },
};

/** The look of each id. The record is typed by the list, so a look the
 *  list gains without its card is a type error. */
export const DEPLOYABLE_COLLECT_LOOKS: Record<DeployableCollectStyle, DeployableCollectLook> = {
  'blueprint-drain': blueprintDrain,
  'straight-haul': straightHaul,
  'winch-lines': winchLines,
  'closing-ring': closingRingDeploy,
  concertina: concertinaLook,
  'tally-tags': tallyTags,
  'weigh-anchors': weighAnchors,
  'timer-ring': timerRing,
  'quiet-take': quietTake,
};

/** The end of the last item's window in one look, at least `tC1`. */
export function deployableCollectEnd(style: DeployableCollectStyle, o: Omit<DeployCollectScene, 'tEnd'>): number {
  const look = DEPLOYABLE_COLLECT_LOOKS[style], scene = { ...o, tEnd: o.tC1 };
  let tEnd = o.tC1;
  for (const it of o.p.aboard) tEnd = Math.max(tEnd, winOf(look, it, scene)[1]);
  return tEnd;
}

/**
 * Paints one deployable collect in one look, as round 074 draws it: the
 * radius (own view), each deployable before, in and after its window, the
 * tell on each item that stays (own view), and the look's effect on the
 * hull. The Sapper itself is the fleet layer's.
 */
export function paintDeployableCollect(c: Ctx, style: DeployableCollectStyle, o: DeployCollectScene): void {
  const look = DEPLOYABLE_COLLECT_LOOKS[style];
  if (o.own) (look.radius ?? defaultRadius)(c, o);
  for (const it of o.p.items) {
    if (it.state !== 'aboard') { still(c, o, it); continue; }
    const w = winOf(look, it, o);
    if (o.t < w[0]) { if (look.wait && o.t >= o.tStop) look.wait(c, it, o); else still(c, o, it); }
    else if (o.t < w[1]) look.item(c, it, (o.t - w[0]) / (w[1] - w[0]), o);
    else look.after?.(c, it, o.t - w[1], o);
  }
  if (o.own) for (const it of o.p.stay) (look.stay ?? defaultStay)(c, it, o);
  look.fx?.(c, o);
}
